import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@material-ui/core';
import { DragIndicator } from '@material-ui/icons';
import { Container, Draggable, DropResult } from 'react-smooth-dnd';
import arrayMove from 'array-move';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { getUserAvatarPath, getUserShortName, USER_OPERATIONS } from 'helper';
import { IOrgStructure, RootState } from 'interfaces';
import useError from 'components/ErrorProvider/useError';
import SubstitutionChip from 'pages/PagePositionMatrix/components/SubstitutionChip';
import { getOrgStructureList, getPositionsBySortOrder, reorderPositions } from './api';

const useStyles = makeStyles({
  root: {
    position: 'relative',
    maxWidth: 1366,
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    zIndex: 1000,
  },
  table: {
    '& thead': {
      '& th': {
        fontWeight: 600,
        backgroundColor: '#FDD7E7',
      },
    },

    '& tbody': {
      '& tr': {
        '& td': {
          minWidth: 250,
          backgroundColor: '#FAFAFA',
          transition: 'background-color 200ms',
        },
        '&:hover': {
          '& td': {
            backgroundColor: '#f5f5f5',
          },
        },
      },
    },
  },
});

interface IProps {
  realEstateId: string;
}

/**
 * Новый общий порядок должностей после перетаскивания строки в одном ЖК.
 *
 * Порядок глобальный (admin_position.sort_order), а в ЖК видна только часть должностей. Должности этого
 * ЖК переставляем между собой на тех же местах общего списка, остальные не трогаем: иначе перестановка
 * в одном ЖК сдвинула бы должности, которых здесь нет, и порядок в других ЖК поехал бы неожиданно.
 */
export const mergePositionOrder = (globalOrder: number[], localRows: IOrgStructure[]): number[] => {
  const localOrder = localRows
    .map((row) => row.positionId)
    .filter((positionId, index, all) => all.indexOf(positionId) === index && globalOrder.includes(positionId));
  const local = new Set(localOrder);
  let next = 0;
  return globalOrder.map((positionId) => (local.has(positionId) ? localOrder[next++] : positionId));
};

const View: React.FC<IProps> = ({ realEstateId }) => {
  // Local state
  const [loading, setLoading] = useState(true);
  const [serviceList, setServiceList] = useState<IOrgStructure[]>([]);

  // Styles
  const classes = useStyles();

  // Global notification
  const { addError } = useError();
  const { enqueueSnackbar } = useSnackbar();

  // порядок общий для всех ЖК — менять его может только администратор
  const operations = useSelector((state: RootState) => state.operations);
  const canReorder = (operations || []).includes(USER_OPERATIONS.ADMIN);
  const [saving, setSaving] = useState(false);

  const fetchList = () =>
    getOrgStructureList(realEstateId)
      .then(setServiceList)
      .catch((error) => {
        addError(error.response.data.message);
      });

  // Перетаскивается должность, а не человек: после сохранения список перечитываем, и все строки
  // с этой должностью встают вместе, люди внутри должности — по ФИО.
  const handleDrop = async ({ removedIndex, addedIndex }: DropResult) => {
    if (saving || removedIndex === null || addedIndex === null || removedIndex === addedIndex) return;
    const reordered = arrayMove(serviceList, removedIndex, addedIndex);
    setServiceList(reordered);
    setSaving(true);
    try {
      const positions = await getPositionsBySortOrder();
      await reorderPositions(
        mergePositionOrder(
          positions.map((position) => position.positionId),
          reordered,
        ),
      );
      enqueueSnackbar('Порядок должностей сохранён', { variant: 'success' });
    } catch (error) {
      addError((error as AxiosError).response?.data?.message);
    } finally {
      await fetchList();
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!realEstateId) return;

    setLoading(true);
    fetchList().finally(() => {
      setLoading(false);
    });
  }, [realEstateId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box className={classes.root}>
      {loading ? (
        <Paper variant="outlined">
          <Box sx={{ position: 'relative', p: 5 }}>
            <Box className={classes.loader}>
              <CircularProgress />
            </Box>
          </Box>
        </Paper>
      ) : serviceList.length ? (
        <TableContainer component={Paper} variant="outlined">
          {canReorder && (
            <Typography variant="body2" color="textSecondary" style={{ padding: '8px 16px' }}>
              Перетащите строку за <DragIndicator style={{ fontSize: 16, verticalAlign: 'middle' }} />, чтобы изменить
              порядок должностей. Порядок общий для всех ЖК и для карточки дома.
            </Typography>
          )}
          <Table stickyHeader size="small" className={classes.table}>
            <Container
              dragHandleSelector=".position-drag-handle"
              lockAxis="y"
              onDrop={handleDrop}
              render={(ref) => (
                <TableBody ref={ref}>
                  {serviceList.map((serviceCompany) => {
                    const { positionName, photoBase64, lastName, firstName, phone, email, substitution } =
                      serviceCompany;

                    return (
                      <Draggable
                        key={`${serviceCompany.userId}-${serviceCompany.positionId}`}
                        render={() => (
                          <TableRow>
                            {canReorder && (
                              <TableCell style={{ minWidth: 0, width: 40, padding: '0 0 0 8px' }}>
                                <Box
                                  className="position-drag-handle"
                                  style={{
                                    cursor: saving ? 'default' : 'grab',
                                    display: 'flex',
                                    opacity: saving ? 0.4 : 1,
                                  }}
                                >
                                  <DragIndicator color="action" />
                                </Box>
                              </TableCell>
                            )}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar src={photoBase64 && getUserAvatarPath(photoBase64)}>
                                  {getUserShortName(lastName, firstName)}
                                </Avatar>

                                <Box sx={{ ml: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography>
                                      {lastName} {firstName}
                                    </Typography>

                                    <Box sx={{ ml: 1 }}>
                                      <SubstitutionChip substitution={substitution} />
                                    </Box>
                                  </Box>

                                  <Typography variant="body2" color="textSecondary">
                                    {positionName}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>

                            <TableCell>{phone}</TableCell>

                            <TableCell>{email}</TableCell>
                          </TableRow>
                        )}
                      />
                    );
                  })}
                </TableBody>
              )}
            />
          </Table>
        </TableContainer>
      ) : null}
    </Box>
  );
};

export default View;
